import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Statistic, Spin } from 'antd';
import { UserOutlined, ShopOutlined, CheckCircleOutlined, BankOutlined, WalletOutlined, GlobalOutlined, ApiOutlined, FileTextOutlined, DollarOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useDashboard } from '../../../context/DashboardContext';
import SessionTimer from '../../SessionTimer/SessionTimer';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import WalletAssignmentsChart from '../../Charts/WalletAssignmentsChart';
import SweepTrendsChart from '../../Charts/SweepTrendsChart';
import WebhookStatsChart from '../../Charts/WebhookStatsChart';
import WalletDeliveryTrendsChart from '../../Charts/WalletDeliveryTrendsChart';
import ActionItems from '../../Dashboard/ActionItems';

const { Title, Paragraph } = Typography;

const DashboardContainer = styled.div`
  padding: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const DashboardTitle = styled(Title)`
  margin-bottom: 0 !important;
  color: #E2E8F0 !important;
  font-size: 24px !important;
  
  @media (max-width: 768px) {
    font-size: 20px !important;
  }
`;

const TimerWrapper = styled.div`
  background: #161B22;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #1f2a37;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const StatCard = styled(Card)`
  background: #161B22 !important;
  border: 1px solid #1f2a37 !important;
  border-radius: 0.75rem !important;
  
  .ant-statistic-title {
    color: #94A3B8 !important;
    font-size: 14px;
  }
  
  .ant-statistic-content {
    color: #E2E8F0 !important;
    font-weight: 700;
  }
  
  &.border-cyan { border-left: 4px solid #22D3EE !important; }
  &.border-purple { border-left: 4px solid #C084FC !important; }
  &.border-amber { border-left: 4px solid #FBBF24 !important; }
  &.border-red { border-left: 4px solid #F87171 !important; }
`;

const Dashboard: React.FC = () => {
  const { sessionExpiryTime, onSessionExpire } = useDashboard();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>({
    totalAdmins: 0,
    totalMerchantUsers: 0,
    totalCompanies: 0,
    totalWallets: 0,
    totalAssignedWallet: 0,
    totalDeliveredWallets: 0,
    totalActiveBlockchains: 0,
    totalCurrencies: 0,
    totalRPCs: 0,
    pendingInvoicesCount: 0,
    pendingInvoicesTotal: 0,
  });

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_DASHBOARD_STATS);
      if (response.data?.status === 'success' && response.data?.data) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardContainer>
      <HeaderRow>
        <DashboardTitle level={2}>Dashboard Overview</DashboardTitle>
        {sessionExpiryTime && (
          <TimerWrapper>
            <span style={{ color: '#94A3B8', fontSize: '14px', fontWeight: 500 }}>Session Expires:</span>
            <SessionTimer expiryTime={sessionExpiryTime} onExpire={onSessionExpire} />
          </TimerWrapper>
        )}
      </HeaderRow>
      
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <ActionItems isAdmin={true} />
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-cyan">
                <Statistic 
                  title="Total Administrators" 
                  value={stats.totalAdmins || 0} 
                  prefix={<UserOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-purple">
                <Statistic 
                  title="Total Merchants" 
                  value={stats.totalMerchantUsers || 0} 
                  prefix={<ShopOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-amber">
                <Statistic 
                  title="Total Companies" 
                  value={stats.totalCompanies || 0} 
                  prefix={<BankOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-red">
                <Statistic 
                  title="Total Factory Inventory" 
                  value={stats.totalWallets || 0} 
                  prefix={<WalletOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-cyan">
                <Statistic 
                  title="Assigned Wallets" 
                  value={stats.totalAssignedWallet || 0} 
                  prefix={<CheckCircleOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-amber">
                <Statistic 
                  title="Delivered Wallets" 
                  value={stats.totalDeliveredWallets || 0} 
                  prefix={<WalletOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-purple">
                <Statistic 
                  title="Active Blockchains" 
                  value={stats.totalActiveBlockchains || 0} 
                  prefix={<GlobalOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-amber">
                <Statistic 
                  title="Total Currencies" 
                  value={stats.totalCurrencies || 0} 
                  prefix={<ShopOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-red">
                <Statistic 
                  title="Total RPCs" 
                  value={stats.totalRPCs || 0} 
                  prefix={<ApiOutlined />} 
                  valueStyle={{ color: '#E2E8F0' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-amber" style={{ borderLeftColor: '#F59E0B' }}>
                <Statistic 
                  title="Pending Invoices" 
                  value={stats.pendingInvoicesCount || 0} 
                  prefix={<FileTextOutlined />} 
                  valueStyle={{ color: '#FBBF24' }}
                />
              </StatCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard className="border-red" style={{ borderLeftColor: '#EF4444' }}>
                <Statistic 
                  title="Total Amount Due" 
                  value={stats.pendingInvoicesTotal || 0} 
                  prefix={<DollarOutlined />} 
                  precision={2}
                  valueStyle={{ color: '#F87171' }}
                />
              </StatCard>
            </Col>
          </Row>
          
          {/* Charts Section */}
          <Row gutter={[24, 24]} style={{ marginTop: 32 }}>
            <Col xs={24} lg={12}>
              <WalletAssignmentsChart />
            </Col>
            <Col xs={24} lg={12}>
              <WalletDeliveryTrendsChart />
            </Col>
            <Col xs={24}>
              <SweepTrendsChart isAdmin={true} />
            </Col>
            <Col xs={24}>
              <WebhookStatsChart isAdmin={true} />
            </Col>
          </Row>
          
          <div style={{ marginTop: 32 }}>
            <Paragraph style={{ color: '#94A3B8' }}>
              Welcome to the Switch Wallet Administrator Dashboard. Use the sidebar to navigate through different sections.
            </Paragraph>
          </div>
        </>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;

